import React, { useEffect, useState, useMemo } from "react";
import { useParams, useNavigate, Link } from "react-router-dom";
import { ArrowLeft, Minus, Plus, Lock } from "lucide-react";
import { toast } from "sonner";
import { api, peso } from "../lib/api";
import { Button } from "../components/ui/button";
import { useCart } from "../context/CartContext";
import { useAuth } from "../context/AuthContext";

export default function ProductDetail() {
  const { id } = useParams();
  const nav = useNavigate();
  const { add } = useCart();
  const { user } = useAuth();
  const [product, setProduct] = useState(null);
  const [qty, setQty] = useState(1);
  const [selectedVariantId, setSelectedVariantId] = useState(null);
  const canSeePrice = !!user;

  useEffect(() => {
    api.get(`/products/${id}`).then((r) => {
      setProduct(r.data);
      // Default to the first variant (Full Sack) if variants exist
      if (r.data?.variants?.length) setSelectedVariantId(r.data.variants[0].id);
    }).catch(() => nav("/shop"));
  }, [id, nav]);

  const selectedVariant = useMemo(() => {
    if (!product?.variants?.length || !selectedVariantId) return null;
    return product.variants.find((v) => v.id === selectedVariantId) || product.variants[0];
  }, [product, selectedVariantId]);

  if (!product) return <div className="max-w-5xl mx-auto py-20 text-center">Loading…</div>;

  const out = product.stock <= 0;
  const displayPrice = selectedVariant ? selectedVariant.price : product.price;
  const displayUnit = selectedVariant ? selectedVariant.unit : product.unit;
  const hasVariants = !!product.variants?.length;

  return (
    <div className="max-w-6xl mx-auto px-4 sm:px-6 lg:px-8 py-6 sm:py-10 page-enter">
      <Link to="/shop" className="inline-flex items-center text-sm text-[#4A5D5A] hover:text-[#2F5233] mb-4 sm:mb-6" data-testid="back-to-shop">
        <ArrowLeft className="h-4 w-4 mr-1" /> Back to shop
      </Link>
      <div className="grid md:grid-cols-2 gap-6 md:gap-10">
        <div className="rounded-2xl overflow-hidden border border-[#E1E5DF] bg-white aspect-square">
          <img src={product.image_url} alt={product.name} className="w-full h-full object-cover" />
        </div>
        <div className="flex flex-col">
          <p className="text-xs italic text-[#8A9591] mb-3" data-testid="image-disclaimer-detail">
            Image is for reference only. Actual product cut &amp; weight may differ.
          </p>
          <div className="overline">{product.category}</div>
          <h1 className="font-heading text-2xl sm:text-3xl lg:text-4xl font-extrabold text-[#1C2826] tracking-tighter mt-1">{product.name}</h1>
          {canSeePrice ? (
            <div className="mt-3 font-heading font-extrabold text-2xl sm:text-3xl text-[#2F5233]" data-testid="detail-price">
              {peso(displayPrice)}
              <span className="text-sm font-normal text-[#738380] ml-2">per {displayUnit}</span>
            </div>
          ) : (
            <div className="mt-3 bg-[#FDF4E8] border border-[#F3C97A] rounded-xl p-4 flex items-start gap-3" data-testid="price-locked-detail">
              <Lock className="h-5 w-5 text-[#C62828] mt-0.5 flex-shrink-0" />
              <div className="text-sm text-[#1C2826]">
                <div className="font-semibold">Prices are visible to registered customers.</div>
                <div className="mt-1 text-[#4A5D5A]">
                  <Link to="/register" className="text-[#2F5233] font-semibold hover:underline">Create a free account</Link> or{" "}
                  <Link to="/login" className="text-[#2F5233] font-semibold hover:underline">log in</Link> with your mobile number to see prices and order.
                </div>
              </div>
            </div>
          )}
          <p className="mt-4 text-[#4A5D5A] leading-relaxed text-sm sm:text-base">{product.description || "Fresh from Arbe Minimart."}</p>

          {/* Variant picker */}
          {canSeePrice && hasVariants && (
            <div className="mt-5" data-testid="variant-picker">
              <div className="overline mb-2">Choose size</div>
              <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
                {product.variants.map((v) => {
                  const active = v.id === selectedVariantId;
                  return (
                    <button
                      key={v.id}
                      onClick={() => { setSelectedVariantId(v.id); setQty(1); }}
                      className={`text-left rounded-xl border-2 px-3 py-2.5 transition ${
                        active
                          ? "border-[#2F5233] bg-[#EAF5EC]"
                          : "border-[#E1E5DF] bg-white hover:border-[#BEDDC2]"
                      }`}
                      data-testid={`variant-${v.id}`}
                    >
                      <div className="text-xs uppercase tracking-wider font-bold text-[#1C2826]">{v.label}</div>
                      <div className={`mt-0.5 font-heading font-extrabold text-base ${active ? "text-[#2F5233]" : "text-[#1C2826]"}`}>
                        {peso(v.price)}<span className="text-xs font-normal text-[#738380] ml-1">/ {v.unit}</span>
                      </div>
                    </button>
                  );
                })}
              </div>
              {selectedVariant?.id === "kilo" && (
                <p className="mt-2 text-xs text-[#738380]">Quantity below is in kilograms (kg).</p>
              )}
            </div>
          )}

          {!hasVariants && (
            <div className="mt-4 text-sm text-[#738380]">Stock: <span className="font-semibold text-[#1C2826]">{product.stock}</span> {product.unit}(s)</div>
          )}

          <div className="mt-6 sm:mt-8 flex items-center gap-3 flex-wrap">
            {canSeePrice ? (
              <>
                <div className="flex items-center border border-[#E1E5DF] rounded-full">
                  <button className="h-11 w-11 flex items-center justify-center text-[#1C2826] hover:bg-[#F1F3EE] rounded-l-full" onClick={() => setQty(Math.max(1, qty - 1))} data-testid="qty-minus">
                    <Minus className="h-4 w-4" />
                  </button>
                  <div className="w-12 text-center font-semibold" data-testid="qty-value">{qty}</div>
                  <button className="h-11 w-11 flex items-center justify-center text-[#1C2826] hover:bg-[#F1F3EE] rounded-r-full" onClick={() => setQty(qty + 1)} data-testid="qty-plus">
                    <Plus className="h-4 w-4" />
                  </button>
                </div>
                <Button
                  disabled={out && !hasVariants}
                  className="bg-[#2F5233] hover:bg-[#1F3D24] text-white rounded-full h-11 px-6"
                  onClick={() => {
                    add(product, qty, selectedVariant);
                    const label = selectedVariant ? ` (${selectedVariant.label})` : "";
                    toast.success(`${qty} × ${product.name}${label} added`);
                  }}
                  data-testid="detail-add-to-cart"
                >
                  Add to cart · {peso(displayPrice * qty)}
                </Button>
              </>
            ) : (
              <Button
                className="bg-[#2F5233] hover:bg-[#1F3D24] text-white rounded-full h-11 px-6"
                onClick={() => nav("/register")}
                data-testid="detail-sign-up"
              >
                Sign up to order
              </Button>
            )}
          </div>
          {out && !hasVariants && <div className="mt-3 text-sm text-[#E74C3C] font-medium">Currently out of stock.</div>}
        </div>
      </div>
    </div>
  );
}
